import ReactDOM from "react-dom/client";
import { DigiCalls } from "@digitalsac/digicalls-sdk";
import { App, type Controller, type Theme, type WebphoneConfig } from "./App";
import styles from "./styles.css?inline";

export type { WebphoneConfig, Theme } from "./App";
export { DigiCalls } from "@digitalsac/digicalls-sdk";

/** Handle returned by render() — lets the host app drive the widget. */
export interface WebphoneHandle {
  dc: DigiCalls;
  setTheme: (t: Theme) => void;
  setLang: (l: "pt" | "en" | "es") => void;
  dial: (to: string) => void;
  open: (open: boolean) => void;
  destroy: () => void;
}

/**
 * DigiCalls webphone widget — renders a full call UI inside a Shadow DOM.
 * Pass a DigiCalls instance, or just a config (it creates one).
 *
 *   import { render } from "@digitalsac/digicalls-widget";
 *   const wp = render({ baseUrl, tokens, theme: "dark" });
 *   wp.setTheme("light");   // host syncs its theme
 *   wp.dial("5511999998888");
 * or via CDN/UMD:
 *   DigiCallsWebphone.render({ baseUrl, tokens });
 *   DigiCallsWebphone.setTheme("dark");
 */
class WebPhoneComponent {
  private container: HTMLElement | null = null;
  private root: ReactDOM.Root | null = null;
  private dc: DigiCalls | null = null;
  private controller: Controller = {};

  render(config: WebphoneConfig, digicalls?: DigiCalls): WebphoneHandle {
    if (this.root) return this.handle();
    this.dc = digicalls ?? new DigiCalls({ baseUrl: config.baseUrl, tokens: config.tokens });
    this.controller = {};

    this.container = document.createElement("div");
    this.container.id = "digicalls-webphone";
    document.body.appendChild(this.container);

    const shadow = this.container.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = styles;
    shadow.appendChild(style);
    const mount = document.createElement("div");
    mount.id = "root";
    shadow.appendChild(mount);

    this.root = ReactDOM.createRoot(mount);
    this.root.render(<App config={config} digicalls={this.dc} controller={this.controller} />);
    return this.handle();
  }

  private handle(): WebphoneHandle {
    return {
      dc: this.dc!,
      setTheme: (t) => this.controller.setTheme?.(t),
      setLang: (l) => this.controller.setLang?.(l),
      dial: (to) => this.controller.dial?.(to),
      open: (o) => this.controller.open?.(o),
      destroy: () => this.destroy(),
    };
  }

  destroy(): void {
    this.dc?.close();
    this.root?.unmount();
    this.container?.remove();
    this.root = null;
    this.container = null;
    this.dc = null;
    this.controller = {};
  }
}

const singleton = new WebPhoneComponent();
let current: WebphoneHandle | null = null;

export const render = (config: WebphoneConfig, digicalls?: DigiCalls): WebphoneHandle => {
  current = singleton.render(config, digicalls);
  return current;
};
export const destroy = (): void => singleton.destroy();
export const setTheme = (t: Theme): void => current?.setTheme(t);
export const setLang = (l: "pt" | "en" | "es"): void => current?.setLang(l);
export const dial = (to: string): void => current?.dial(to);
export { WebPhoneComponent };
export default { render, destroy, setTheme, setLang, dial, WebPhoneComponent };
